import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import { Shield, Camera, Lock } from 'lucide-react';
import { API } from '@/config/api';

export default function ViewerAuth() {
  const { udtId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('otp'); // otp, face, verified
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [camError, setCamError] = useState('');
  const [camKey, setCamKey] = useState(0);
  const webcamRef = useRef(null);

  useEffect(() => {
    // Get token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('token');
    if (accessToken) {
      setToken(accessToken);
    } else {
      toast.error('Invalid access link');
    }
  }, []);

  const handleOTPVerify = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter valid 6-digit OTP');
      return;
    }

    // For demo, accept 123456 as OTP
    if (otp === '123456') {
      toast.success('OTP verified');
      setStep('face');
    } else {
      toast.error('Invalid OTP. Use 123456 for demo');
    }
  };

  const handleFaceVerify = async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      toast.error('Unable to capture face');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/viewer/verify-access`, {
        udt_id: udtId,
        token: token,
        face_image: imageSrc
      });

      toast.success('Face verified! Redirecting to document...');
      setStep('verified');
      
      // Redirect to actual viewer
      setTimeout(() => {
        navigate(`/viewer/${udtId}?token=${token}&verified=true`);
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Face verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #E8F2FF 0%, #F5F9FF 100%)' }}>
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8" style={{ color: '#0F52BA' }} />
            <div>
              <CardTitle>Secure Access Verification</CardTitle>
              <CardDescription>UDT ID: {udtId}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 'otp' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="otp">Enter OTP</Label>
                <Input
                  id="otp"
                  data-testid="viewer-otp-input"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                />
                <p className="text-sm" style={{ color: '#666' }}>Demo OTP: 123456</p>
              </div>
              <Button
                data-testid="verify-otp-viewer"
                onClick={handleOTPVerify}
                className="w-full"
                style={{ background: '#0F52BA' }}
              >
                <Lock className="w-4 h-4 mr-2" />
                Verify OTP
              </Button>
            </>
          )}

          {step === 'face' && (
            <>
              <div className="space-y-3">
                <Label>Face Verification</Label>
                <div className="relative rounded-lg overflow-hidden" style={{ border: '3px solid #0F52BA' }}>
                  <Webcam
                    key={camKey}
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    className="w-full"
                    videoConstraints={{ facingMode: 'user' }}
                    onUserMedia={() => setCamError('')}
                    onUserMediaError={(err) => {
                      // Common causes:
                      // - user denied permission
                      // - camera already in use by another app/tab
                      // - OS camera privacy settings disabled
                      setCamError(err?.name || 'Camera permission blocked');
                    }}
                  />
                </div>
                {camError && (
                  <div className="text-sm text-red-600">
                    Camera error: {camError}. Please allow camera permission for this site and close other apps using the camera.
                    <div className="mt-2">
                      <Button variant="outline" onClick={() => { setCamKey((k) => k + 1); setCamError(''); }}>
                        Retry camera
                      </Button>
                    </div>
                  </div>
                )}
                <p className="text-sm text-center" style={{ color: '#666' }}>
                  Position your face in the camera. Only ONE face should be visible.
                </p>
              </div>
              <Button
                data-testid="verify-face-viewer"
                onClick={handleFaceVerify}
                disabled={loading}
                className="w-full"
                style={{ background: '#10b981' }}
              >
                <Camera className="w-4 h-4 mr-2" />
                {loading ? 'Verifying...' : 'Verify Face & Access Document'}
              </Button>
            </>
          )}

          {step === 'verified' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-semibold" style={{ color: '#10b981' }}>Verified Successfully!</p>
              <p className="text-sm" style={{ color: '#666' }}>Redirecting to document...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
